import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'

interface ThreadsFromMyGroupsProps {
  userId: string
}

interface Thread {
  id: string
  title: string
  body: string
  created_at: string
  group_id: string
  groups: {
    id: string
    name: string
  }
  comment_count: number
  latest_comment_date: string | null
}

export default function ThreadsFromMyGroups({ userId }: ThreadsFromMyGroupsProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)

  const { data: threads, isLoading } = useQuery({
    queryKey: ['threads-from-my-groups', userId],
    queryFn: async () => {
      try {
        // First get user's group IDs (excluding vendor groups)
        const { data: memberships, error: membershipsError } = await supabase
          .from('group_members')
          .select(`
            group_id,
            groups (
              id,
              group_type
            )
          `)
          .eq('user_id', userId)

        if (membershipsError) {
          console.warn('Error fetching group memberships:', membershipsError)
          return []
        }

        if (!memberships || memberships.length === 0) {
          return []
        }

        // Filter out vendor groups and get only normal user group IDs
        const groupIds = memberships
          .filter((m: any) => {
            const group = m.groups
            // Only include groups with no group_type or group_type that's not broadcaster/ad-creator
            return !group?.group_type || (group.group_type !== 'broadcaster' && group.group_type !== 'ad-creator')
          })
          .map((m: any) => m.group_id)

        // Get threads from user's groups
        const { data: threadsData, error } = await supabase
          .from('threads')
          .select(`
            id,
            title,
            body,
            created_at,
            group_id,
            groups (
              id,
              name
            )
          `)
          .in('group_id', groupIds)
          .eq('is_hidden', false)
          .order('created_at', { ascending: false })
          .limit(10)

        if (error) {
          console.warn('Error fetching threads:', error)
          return []
        }

        // Get comment counts and latest comment dates, transform data
        const threadsWithComments = await Promise.all(
          (threadsData || []).map(async (thread: any) => {
            try {
              const { count } = await supabase
                .from('comments')
                .select('*', { count: 'exact', head: true })
                .eq('thread_id', thread.id)
                .eq('is_hidden', false)

              const { data: latestComment } = await supabase
                .from('comments')
                .select('created_at')
                .eq('thread_id', thread.id)
                .eq('is_hidden', false)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle()

              // Transform data: Supabase returns groups as array, extract first element
              return {
                ...thread,
                groups: Array.isArray(thread.groups)
                  ? (thread.groups[0] || null)
                  : thread.groups,
                comment_count: count || 0,
                latest_comment_date: latestComment?.created_at || null,
              }
            } catch (err) {
              console.warn('Error fetching comment data:', err)
              return {
                ...thread,
                groups: Array.isArray(thread.groups)
                  ? (thread.groups[0] || null)
                  : thread.groups,
                comment_count: 0,
                latest_comment_date: null,
              }
            }
          })
        )

        return threadsWithComments as Thread[]
      } catch (err) {
        console.warn('Error in threads query:', err)
        return []
      }
    },
    retry: false,
  })

  if (isCollapsed) {
    return (
      <div 
        className="rounded-lg p-6 border-2"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--bg-tertiary)',
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 
              className="text-xl font-bold mb-1"
              style={{ color: 'var(--text-primary)' }}
            >
              Threads from My Groups
            </h2>
            <p 
              className="text-sm"
              style={{ color: 'var(--text-primary)' }}
            >
              Activity from groups you've joined
            </p>
          </div>
          <button
            onClick={() => setIsCollapsed(false)}
            className="px-4 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
          >
            Expand
          </button>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div 
        className="rounded-lg p-6 border-2"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--bg-tertiary)',
        }}
      >
        <p style={{ color: 'var(--text-primary)' }}>Loading threads...</p>
      </div>
    )
  }

  return (
    <div 
      className="rounded-lg p-6 border-2"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: 'var(--bg-tertiary)',
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h2 
            className="text-xl font-bold mb-1"
            style={{ color: 'var(--text-primary)' }}
          >
            Threads from My Groups
          </h2>
          <p 
            className="text-sm"
            style={{ color: 'var(--text-primary)' }}
          >
            Activity from groups you've joined
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/activity"
            className="px-4 py-2 font-semibold rounded underline"
            style={{ color: 'var(--text-link)' }}
          >
            View all
          </Link>
          <button
            onClick={() => setIsCollapsed(true)}
            className="px-4 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
          >
            Collapse
          </button>
        </div>
      </div>

      {!threads || threads.length === 0 ? (
        <p style={{ color: 'var(--text-primary)' }}>
          No threads from your groups yet.
        </p>
      ) : (
        <div className="space-y-4">
          {threads.map((thread) => {
            const group = thread.groups
            if (!group) return null

            const latestCommentDate = thread.latest_comment_date
              ? format(new Date(thread.latest_comment_date), 'dd/MM/yyyy, HH:mm:ss')
              : null

            return (
              <div
                key={thread.id}
                className="p-4 rounded border-2"
                style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  borderColor: 'var(--bg-tertiary)',
                }}
              >
                <h3 
                  className="text-lg font-bold mb-2"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {thread.title}
                </h3>
                <Link
                  to={`/groups/${group.id}`}
                  className="text-sm mb-2 block"
                  style={{ color: 'var(--text-link)' }}
                >
                  {group.name}
                </Link>
                <p 
                  className="mb-3"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {thread.body.length > 200 
                    ? `${thread.body.substring(0, 200)}...` 
                    : thread.body}
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <span>{thread.comment_count} comments</span>
                    {latestCommentDate && (
                      <span className="ml-4">
                        Latest comment: {latestCommentDate}
                      </span>
                    )}
                  </div>
                  <Link
                    to={`/groups/${group.id}`}
                    state={{ selectedThreadId: thread.id }}
                    className="px-4 py-2 font-semibold rounded"
                    style={{
                      backgroundColor: 'var(--bg-interactive)',
                      color: 'var(--text-inverse)',
                    }}
                  >
                    View Thread
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

